/**
 * Arrow element renderer
 * Extracted from Canvas.jsx to reduce file size
 * Now uses UnifiedLineArrowElement for unified handling of lines, arrows, and double-sided arrows
 */
import React from "react";
import UnifiedLineArrowElement from "../../unified/UnifiedLineArrowElement";
import { isElementInViewport } from "../../../utils/viewportCullingUtils";

/**
 * Creates an arrow element renderer factory function
 */
export const createArrowRenderer = ({
  arrows,
  offCanvasArrows,
  setArrows,
  setOffCanvasArrows,
  selectedObject,
  strokeColor,
  effectiveZoomLevel,
  disableInteraction,
  debouncedStateUpdate,
  isTransformingRef,
  setSelectedObject,
  setSelectedObjects,
  selectedObjects = [],
  setShowTextBox,
  setSelectedText,
  setSelectedImage,
  setSelectedArrow,
  setSelectedDoubleSidedArrow,
  setSelectedLine,
  onClick,
  onDoubleClick,
  handleElementDragEnd,
  handleArrowDragEnd,
  saveToHistory,
  handleDragPreview,
  clearDragPreview,
  handleElementCopy,
  canvasWidth,
  canvasHeight,
  lockedAction,
  isCloneMode = false, // Clone mode flag (additional check for clone mode)
  isElementBeingEdited = false,
  setPendingChanges = () => { },
  isMultiSelectMode = false, // Multi-select mode flag
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  cancelActiveGroupDrag = null,
  // Element arrays for cross-canvas selection check
  rectangles = [],
  offCanvasRectangles = [],
  circles = [],
  offCanvasCircles = [],
  images = [],
  offCanvasImages = [],
  texts = [],
  offCanvasTexts = [],
  lines = [],
  offCanvasLines = [],
  doubleSidedArrows = [],
  offCanvasDoubleSidedArrows = [],
  blockClicksOnOffCanvasElements = false,
  blockClicksOnMainStageElements = false,
  stageRef = null,
  offCanvasStageRef = null,
}) => {
  return ({
    arrow,
    sourceStageRef,
    isOffCanvas = false,
    isPreview = false,
    opacity = undefined, // Opacity override for dimming non-selected elements
  }) => {
    // Helper function to check if an element is in main canvas
    const isElementInMainCanvas = (elementId, elementType) => {
      if (!elementId || !elementType) return false;
      switch (elementType) {
        case "Rect":
          return rectangles.some((r) => r.id === elementId);
        case "Circle":
          return circles.some((c) => c.id === elementId);
        case "Image":
          return images.some((i) => i.id === elementId);
        case "Text":
          return texts.some((t) => t.id === elementId);
        case "Arrow":
          return arrows.some((a) => a.id === elementId);
        case "Line":
          return lines.some((l) => l.id === elementId);
        case "DoubleSidedArrow":
          return doubleSidedArrows.some((d) => d.id === elementId);
        default:
          return false;
      }
    };

    // Use provided opacity or element's own opacity, defaulting to 1
    const elementOpacity =
      opacity !== undefined
        ? opacity
        : arrow.opacity !== undefined
          ? arrow.opacity
          : 1;
    // Only allow dragging if this arrow is selected
    const isSelected =
      !isPreview &&
      arrow.id === selectedObject?.id &&
      (selectedObject?.type === "Arrow" ||
        selectedObject?.type === "Line" ||
        selectedObject?.type === "DoubleSidedArrow");

    // Check if element is in selectedObjects array (for multi-selection)
    const isInSelectedObjects = selectedObjects && selectedObjects.some(
      (obj) => obj.id === arrow.id && (
        obj.type === "Arrow" ||
        obj.type === "Line" ||
        obj.type === "DoubleSidedArrow"
      )
    );

    // When duplicate-to-selected-location is on, no drag (single or group). Otherwise draggable if selected or in multi-selection.
    const isDraggableElement = !isCloneMode && !lockedAction && (isSelected || (isInSelectedObjects && selectedObjects.length > 1));

    const setArrowState = isOffCanvas ? setOffCanvasArrows : setArrows;
    const isInteractive = !isPreview;

    // CRITICAL: If this arrow is selected, merge properties from selectedObject to ensure we have the latest values
    // This is important because selectedObject is updated immediately, but array updates might be batched or delayed
    const effectiveArrow = isSelected && selectedObject && selectedObject.id === arrow.id
      ? { ...arrow, ...selectedObject }
      : arrow;

    // Convert arrow to unified element format
    // For Arrow type: hasEndArrow = true, hasStartArrow = false (unless explicitly set)
    const unifiedElement = {
      ...effectiveArrow,
      opacity: elementOpacity,
      hasStartArrow:
        effectiveArrow.hasStartArrow !== undefined
          ? effectiveArrow.hasStartArrow
          : effectiveArrow.pointerAtBeginning || false,
      hasEndArrow:
        effectiveArrow.hasEndArrow !== undefined
          ? effectiveArrow.hasEndArrow
          : effectiveArrow.pointerAtEnd !== undefined
            ? effectiveArrow.pointerAtEnd
            : true, // Default to true for Arrow type
      type: effectiveArrow.type || "Arrow", // Preserve type for selection logic
    };

    // CRITICAL: Double-check merge with selectedObject to ensure latest values
    const elementToUse = isSelected && selectedObject && selectedObject.id === unifiedElement.id
      ? { ...unifiedElement, ...selectedObject }
      : unifiedElement;

    // CRITICAL: Recompute arrow flags from elementToUse to ensure key changes when flags change
    const finalHasStartArrow = elementToUse.hasStartArrow === true;
    const finalHasEndArrow = elementToUse.hasEndArrow === true;

    // Helper function to update arrow properties
    const updateArrowProperties = (arrowId, updates) => {
      setArrowState((prev) =>
        prev.map((a) => (a.id === arrowId ? { ...a, ...updates } : a))
      );
    };

    // Viewport culling: skip rendering arrows/lines far outside visible area
    const shouldAlwaysRender = isPreview || isSelected || isInSelectedObjects;
    if (!shouldAlwaysRender) {
      const stage = isOffCanvas ? offCanvasStageRef?.current : stageRef?.current;
      if (stage && !isElementInViewport(elementToUse, "Arrow", stage)) {
        return null;
      }
    }

    return (
      <UnifiedLineArrowElement
        key={`${arrow.id}-${finalHasStartArrow}-${finalHasEndArrow}`}
        element={elementToUse}
        strokeColor={arrow.stroke || arrow.strokeColor || strokeColor}
        isDraggable={isInteractive && !disableInteraction && isDraggableElement}
        isSelected={isSelected}
        lockedAction={lockedAction}
        isCloneMode={isCloneMode}
        selectedObjectsArray={selectedObjects || []}
        onSelect={
          setSelectedObject
            ? (e, selectedElement) => {
              // Handle selection similar to lineRenderer
              // Disable selection if a tool is locked or clone mode is active
              if (lockedAction || isCloneMode) {
                return;
              }

              // When multi-select is on and selection is from the other stage, prevent clicking on this stage's elements
              if (isOffCanvas && blockClicksOnOffCanvasElements) {
                e?.evt?.preventDefault?.();
                e?.evt?.stopPropagation?.();
                return;
              }
              if (!isOffCanvas && blockClicksOnMainStageElements) {
                e?.evt?.preventDefault?.();
                e?.evt?.stopPropagation?.();
                return;
              }

              const isSameElement =
                selectedObject && selectedObject.id === selectedElement.id;

              // CRITICAL: When multi-select mode is enabled, toggle membership in selectedObjects
              if (isMultiSelectMode) {
                // Build current selection
                const currentSelectionArray = [];
                const currentSelectionSet = new Set();

                // First, add all items from selectedObjects
                if (selectedObjects && selectedObjects.length > 0) {
                  selectedObjects.forEach((obj) => {
                    if (!currentSelectionSet.has(obj.id)) {
                      currentSelectionArray.push(obj);
                      currentSelectionSet.add(obj.id);
                    }
                  });
                }

                // Then, ensure selectedObject is included
                if (
                  selectedObject &&
                  !currentSelectionSet.has(selectedObject.id)
                ) {
                  currentSelectionArray.push(selectedObject);
                  currentSelectionSet.add(selectedObject.id);
                }

                // Toggle off when already selected
                if (currentSelectionSet.has(selectedElement.id)) {
                  cancelActiveGroupDrag?.();
                  const nextSelection = currentSelectionArray.filter(
                    (obj) => obj.id !== selectedElement.id,
                  );
                  const nextPrimary = nextSelection[0] || null;
                  setSelectedObjects(nextSelection);
                  setSelectedObject(nextPrimary);
                  setSelectedArrow?.(
                    nextPrimary &&
                      (nextPrimary.type === "Arrow" ||
                        nextPrimary.type === "Line" ||
                        nextPrimary.type === "DoubleSidedArrow")
                      ? nextPrimary
                      : null,
                  );
                  let n = e?.target;
                  while (n) {
                    if (typeof n.stopDrag === "function") {
                      try {
                        n.stopDrag();
                      } catch (_) {
                        /* ignore */
                      }
                      break;
                    }
                    n = n.getParent?.() ?? null;
                  }
                  return;
                }

                // Cross-canvas selection check
                if (currentSelectionArray.length > 0) {
                  const selectedElementsCanvasTypes =
                    currentSelectionArray.map((obj) =>
                      isElementInMainCanvas(obj.id, obj.type)
                    );
                  const allFromMainCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === true
                  );
                  const allFromOffCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === false
                  );

                  const elementType = selectedElement.type || "Arrow";
                  const clickedArrowIsMainCanvas = isElementInMainCanvas(
                    selectedElement.id,
                    elementType
                  );

                  if (
                    (allFromMainCanvas && !clickedArrowIsMainCanvas) ||
                    (allFromOffCanvas && clickedArrowIsMainCanvas)
                  ) {
                    if (isMultiSelectMode) {
                      // In multi-select mode, don't allow selecting elements from different stages
                      if (e) {
                        e.evt?.stopPropagation?.();
                        e.evt?.preventDefault?.();
                      }
                      return;
                    } else {
                      // Normal mode cross-canvas behavior:
                      // first click clears current selection only.

                      // Clear all current selections
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setShowTextBox(false);
                      setSelectedText(null);
                      if (setSelectedArrow) {
                        setSelectedArrow(null);
                      }
                      if (setSelectedDoubleSidedArrow) {
                        setSelectedDoubleSidedArrow(null);
                      }
                      if (setSelectedLine) {
                        setSelectedLine(null);
                      }
                      if (e) {
                        e.evt?.stopPropagation?.();
                        e.evt?.preventDefault?.();
                        e.cancelBubble = true;
                      }
                      return;
                    }
                  }

                  // Add the new element to the selection (only if we didn't just clear selections or prevent in multi-select)
                  const shouldAddToSelection = !((allFromMainCanvas && !clickedArrowIsMainCanvas) || (allFromOffCanvas && clickedArrowIsMainCanvas));
                  const nextSelection = shouldAddToSelection ? [
                    ...currentSelectionArray,
                    selectedElement,
                  ] : [selectedElement]; // If we cleared selections, start fresh with just this element

                  setSelectedImage(null);
                  setShowTextBox(false);
                  setSelectedText(null);
                  setSelectedArrow?.(selectedElement);
                  setSelectedObject(selectedElement);
                  if (setSelectedObjects) {
                    setSelectedObjects(nextSelection);
                  }
                } else {
                  // No current selection, just add this element
                  setSelectedImage(null);
                  setShowTextBox(false);
                  setSelectedText(null);
                  setSelectedArrow?.(selectedElement);
                  setSelectedObject(selectedElement);
                  if (setSelectedObjects) {
                    setSelectedObjects([selectedElement]);
                  }
                }
                // CRITICAL: Stop event propagation to prevent click handler from overriding selection
                // The onSelect handler has already set the correct type, so we don't want the click handler to override it
                if (e) {
                  e.evt?.stopPropagation?.();
                  e.evt?.preventDefault?.();
                  e.evt?.stopImmediatePropagation?.();
                  // Don't call onClick - onSelect has already handled the selection
                }
                return;
              }

              // CRITICAL: When multi-select mode is enabled, don't clear selection on first click
              if (
                selectedObject &&
                e &&
                !(e.evt && e.evt.shiftKey) &&
                !isSameElement &&
                !isMultiSelectMode
              ) {
                setSelectedObject(null);
                setSelectedObjects([]);
                setShowTextBox(false);
                setSelectedText(null);
                return;
              }

              if (isSameElement && e) {
                return;
              }

              if (
                !selectedObject ||
                selectedObject.id === selectedElement.id
              ) {
                // Cross-canvas selection check
                const allSelectedElements = [];
                if (
                  setSelectedObjects &&
                  selectedObjects &&
                  selectedObjects.length > 0
                ) {
                  selectedObjects.forEach((obj) => {
                    if (obj && obj.id && obj.type) {
                      allSelectedElements.push(obj);
                    }
                  });
                }
                if (
                  selectedObject &&
                  selectedObject.id &&
                  selectedObject.type
                ) {
                  const alreadyIncluded = allSelectedElements.some(
                    (obj) => obj.id === selectedObject.id
                  );
                  if (!alreadyIncluded) {
                    allSelectedElements.push(selectedObject);
                  }
                }

                if (allSelectedElements.length > 0) {
                  const selectedElementsCanvasTypes = allSelectedElements.map(
                    (obj) => isElementInMainCanvas(obj.id, obj.type)
                  );
                  const allFromMainCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === true
                  );
                  const allFromOffCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === false
                  );

                  const elementType = selectedElement.type || "Arrow";
                  const clickedArrowIsMainCanvas = isElementInMainCanvas(
                    selectedElement.id,
                    elementType
                  );

                  if (
                    (allFromMainCanvas && !clickedArrowIsMainCanvas) ||
                    (allFromOffCanvas && clickedArrowIsMainCanvas)
                  ) {
                    if (isMultiSelectMode) {
                      // In multi-select mode, don't allow selecting elements from different stages
                      e?.evt?.stopPropagation?.();
                      e?.evt?.preventDefault?.();
                      return;
                    } else {
                      // Normal mode cross-canvas behavior:
                      // first click clears current selection only.

                      // Clear all current selections
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setShowTextBox(false);
                      setSelectedText(null);
                      if (setSelectedArrow) {
                        setSelectedArrow(null);
                      }
                      if (setSelectedDoubleSidedArrow) {
                        setSelectedDoubleSidedArrow(null);
                      }
                      if (setSelectedLine) {
                        setSelectedLine(null);
                      }
                      e?.evt?.stopPropagation?.();
                      e?.evt?.preventDefault?.();
                      e.cancelBubble = true;
                      return;
                    }
                  }
                }

                setSelectedImage(null);
                setShowTextBox(false);
                setSelectedText(null);
                setSelectedArrow?.(selectedElement);
                setSelectedObject(selectedElement);
                // CRITICAL: Stop event propagation to prevent click handler from overriding selection
                // The onSelect handler has already set the correct type, so we don't want the click handler to override it
                if (e) {
                  e.evt?.stopPropagation?.();
                  e.evt?.preventDefault?.();
                  e.evt?.stopImmediatePropagation?.();
                  // Don't call onClick - onSelect has already handled the selection
                }
              }
            }
            : undefined
        }
        onClick={isInteractive ? onClick : undefined}
        sourceStageRef={sourceStageRef}
        effectiveZoomLevel={effectiveZoomLevel}
        onDragMove={
          isInteractive
            ? (element, event) => {
              // If multiple elements are selected AND this element is part of the selection,
              // use group drag handler instead of individual drag
              const elementType = element.type || "Arrow";
              const isInSelectedObjects = selectedObjects?.some(
                (obj) => obj.id === element.id && obj.type === elementType
              );
              if (selectedObjects && selectedObjects.length > 1 && isInSelectedObjects) {
                // Initialize group drag on first move (if not already initialized)
                handleGroupDragStart?.(element.id, elementType);
                // Use group drag handler - pass event for accurate boundary detection
                handleGroupDragMove?.(element.id, elementType, element.x, element.y, event);
                // Still update the dragged element's visual state
                setSelectedObject({
                  ...element,
                  type: elementType,
                });
                return;
              }

              // Normal individual drag behavior
              handleDragPreview?.({
                event,
                newAttrs: element,
                sourceStageRef,
                element: arrow,
                isOffCanvas,
                type: element.type || "Arrow",
              });
              setSelectedObject({
                ...element,
                type: element.type || "Arrow",
              });
              setSelectedObjects([
                {
                  ...element,
                  type: element.type || "Arrow",
                },
              ]);
              updateArrowProperties(arrow.id, {
                ...element,
                skipOffset: true,
                width: element.width,
                height: element.height,
              });
            }
            : undefined
        }
        onDragEnd={
          isInteractive
            ? (
              id,
              newX,
              newY,
              sourceStageRefParam,
              sourceStageRef,
              currentArrow = null
            ) => {
              clearDragPreview?.();

              // CRITICAL: Get the current arrow properties from the Konva node if not provided
              let arrowWithRotation = currentArrow;
              if (!arrowWithRotation) {
                const stage = sourceStageRef?.current || sourceStageRef;
                if (stage) {
                  const node = stage.findOne(`#${id}`);
                  if (node) {
                    const baseArrow = (
                      isOffCanvas ? offCanvasArrows : arrows
                    ).find((arrow) => arrow.id === id);

                    if (baseArrow) {
                      let nodeRotation = node.rotation();
                      nodeRotation = nodeRotation % 360;
                      if (nodeRotation < 0) nodeRotation += 360;

                      arrowWithRotation = {
                        ...baseArrow,
                        x: newX,
                        y: newY,
                        rotation: nodeRotation,
                        scaleX: node.scaleX(),
                        scaleY: node.scaleY(),
                      };
                    }
                  }
                }
              }

              // Fallback: if we couldn't get the node, use the arrow from state
              if (!arrowWithRotation) {
                arrowWithRotation = (
                  isOffCanvas ? offCanvasArrows : arrows
                ).find((arrow) => arrow.id === id);
                if (arrowWithRotation) {
                  arrowWithRotation = {
                    ...arrowWithRotation,
                    x: newX,
                    y: newY,
                  };
                }
              }

              handleElementDragEnd({
                id,
                newX,
                newY,
                type: "Arrow",
                sourceStageRef: sourceStageRefParam || sourceStageRef,
                mainState: arrows,
                offCanvasState: offCanvasArrows,
                setMainState: setArrows,
                setOffCanvasState: setOffCanvasArrows,
                setSelectedObjects,
                currentArrow: arrowWithRotation,
              });
            }
            : undefined
        }
        onUpdate={
          isInteractive
            ? (
              id,
              absPoints,
              bends,
              angle,
              groupX,
              groupY,
              width,
              height,
              e
            ) => {
              // Convert absolute points to relative coordinates
              const relativePoints = absPoints.map((coord, i) =>
                i % 2 === 0 ? coord - groupX : coord - groupY
              );

              const updatedElement = {
                ...arrow,
                id,
                points: relativePoints,
                x: groupX,
                y: groupY,
                width,
                height,
                angle,
              };
              setArrowState((prev) =>
                prev.map((a) => (a.id === id ? updatedElement : a))
              );
              setSelectedObject({
                ...updatedElement,
                type: updatedElement.type || "Arrow",
              });
              setSelectedArrow?.(updatedElement);
            }
            : undefined
        }
        updateElementProperties={updateArrowProperties}
        isOffCanvas={isOffCanvas}
        isPreview={isPreview}
        hideBlueCircles={isMultiSelectMode && selectedObjects && selectedObjects.length > 1}
        isMultiSelectMode={isMultiSelectMode}
        selectedObjectsArray={selectedObjects || []}
        onDotDragMove={
          isInteractive
            ? (updatedElement) => {
              setSelectedObject({
                ...updatedElement,
                type: updatedElement.type || "Arrow",
              });
              setArrowState((prev) =>
                prev.map((a) =>
                  a.id === updatedElement.id ? updatedElement : a
                )
              );
              setSelectedArrow?.(updatedElement);
            }
            : undefined
        }
        saveToHistory={saveToHistory}
        onCopyToOppositeCanvas={
          isInteractive && handleElementCopy && isOffCanvas
            ? () => handleElementCopy(arrow.id, "Arrow", "offCanvas")
            : undefined
        }
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
      />
    );
  };
};
